/**
 * Las variantes de un producto físico: sus opciones —talla, color…— y, de cada
 * combinación, cuántas quedan, su SKU y si se vende.
 *
 * Controlado: el borrador vive en quien lo usa —el paso «Variantes» del
 * asistente— y se guarda con el resto al terminar, de una vez. Cambiar una
 * opción rehace las combinaciones, y guardar a medias dejaría la tienda con
 * tallas sin colores. Las combinaciones que siguen conservan sus existencias
 * (el servidor las reconoce por sus valores, ver `services/variants.ts`).
 *
 * Tres partes, en el orden en que se decide:
 *   · sin opciones, una portada con atajos —Talla, Color, Material, Otra—:
 *     casi todo lo que se vende con variantes empieza por una de esas;
 *   · cada opción en su tarjeta, con sus valores como fichas y sugerencias
 *     que se añaden de un toque (y una muestra de color si es un color);
 *   · el inventario: cuántas variantes y unidades, existencias para todas de
 *     una vez, SKU generados y una fila por combinación, agrupadas por el
 *     primer valor cuando hay más de una opción.
 *
 * Cada combinación cuesta lo que el producto salvo que se le ponga un precio
 * propio (la columna «Precio»: vacía = el del producto). De cada color se
 * puede elegir su tono —pulsando la muestra— y de cada valor la foto que se
 * enseña al elegirlo en la tienda. Con variantes, las existencias del
 * producto dejan de mandar: cuenta lo de cada combinación.
 */
import { useRef, useState } from 'react'
import {
  IconBox,
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconColorSwatch,
  IconPlus,
  IconRuler2,
  IconSparkles,
  IconStack2,
  IconTag,
  IconTrash,
  IconX,
} from '@tabler/icons-react'
import type { Offer, OfferVariant, OfferVariantOption } from '../../../lib/api'
import { useI18n } from '../../../lib/i18n'
import { formatMoney, minorUnitFactor, toMinorUnits } from './offerShared'
import { VariantDialog } from './VariantDialog'

/** Los mismos topes que el servidor (`services/variants.ts`). */
export const MAX_VARIANT_OPTIONS = 3
const MAX_VALUES = 30
export const MAX_VARIANT_COMBINATIONS = 100

/**
 * Lo de cada combinación, como se escribe. `price` vacío = el del producto;
 * `compareAt` es el «antes» tachado, `cost` lo que le cuesta al negocio (sólo
 * para el margen), `image` su foto propia (vacía = la de su color o la del
 * producto). Ver `VariantDialog`.
 */
export type VariantRow = { stock: string; sku: string; active: boolean; price: string; compareAt: string; cost: string; barcode: string; image: string }

/** Lo que se está editando: las opciones, lo de cada combinación y lo escrito sin confirmar. */
export type VariantsDraft = { options: OfferVariantOption[]; rows: Record<string, VariantRow>; pending: string[] }

/** La llave de una combinación: la misma aunque cambien mayúsculas o espacios. */
export const variantKeyOf = (values: string[]) => values.map((value) => value.trim().toLowerCase()).join('\u0001')

export function combinationsOf(options: OfferVariantOption[]): string[][] {
  const usable = options.filter((option) => option.name.trim() && option.values.length)
  if (!usable.length) return []
  return usable.reduce<string[][]>((acc, option) => acc.flatMap((prefix) => option.values.map((value) => [...prefix, value])), [[]])
}

/** Un importe guardado (centavos) como se escribe, con los decimales de su moneda. */
function priceText(amount: number | null | undefined, currency: string): string {
  if (amount === null || amount === undefined) return ''
  const factor = minorUnitFactor(currency)
  return (amount / factor).toFixed(factor === 1 ? 0 : 2)
}

export function variantsDraftOf(offer: Pick<Offer, 'variantOptions' | 'variants'> | null | undefined, currency = 'usd'): VariantsDraft {
  return {
    options: offer?.variantOptions ?? [],
    rows: Object.fromEntries((offer?.variants ?? []).map((variant) => [
      variantKeyOf(variant.options),
      {
        stock: variant.stock === null ? '' : String(variant.stock), sku: variant.sku, active: variant.active,
        price: priceText(variant.price, currency), compareAt: priceText(variant.compareAtPrice, currency), cost: priceText(variant.cost, currency),
        barcode: variant.barcode ?? '', image: variant.image ?? '',
      },
    ])),
    pending: [],
  }
}

/** Añade a cada opción lo escrito sin pulsar Intro, sin repetidos. */
function withPending(draft: VariantsDraft): OfferVariantOption[] {
  return draft.options.map((option, index) => ({ ...option, values: addValues(option.values, draft.pending[index] ?? '') }))
}

/** Lo de cada valor (tono, foto) sólo de los valores que siguen; nada si no queda nada. */
function keepFor(map: Record<string, string> | undefined, values: string[]): Record<string, string> | undefined {
  if (!map) return undefined
  const kept = Object.fromEntries(values.filter((value) => map[value]).map((value) => [value, map[value]!]))
  return Object.keys(kept).length ? kept : undefined
}

/** Los valores de `text` (separados por comas) añadidos a `values`, sin repetidos ni pasar del tope. */
function addValues(values: string[], text: string): string[] {
  const seen = new Set(values.map((value) => value.toLowerCase()))
  const out = [...values]
  for (const value of text.split(',').map((part) => part.trim()).filter(Boolean)) {
    if (seen.has(value.toLowerCase()) || out.length >= MAX_VALUES) continue
    seen.add(value.toLowerCase())
    out.push(value.slice(0, 40))
  }
  return out
}

/** El borrador con lo escrito ya confirmado como valores: lo hace el asistente al cambiar de paso. */
export function commitPendingVariants(draft: VariantsDraft): VariantsDraft {
  return draft.pending.some((text) => text.trim()) ? { ...draft, options: withPending(draft), pending: [] } : draft
}

/**
 * El borrador listo para guardar, o la clave del motivo por el que no lo está.
 * `fresh` es cómo arranca una combinación que nadie ha tocado.
 */
export function finalizeVariants(draft: VariantsDraft, fresh: VariantRow, currency = 'usd'):
  | { ok: true; options: OfferVariantOption[]; variants: Pick<OfferVariant, 'options' | 'stock' | 'sku' | 'active' | 'price' | 'image' | 'compareAtPrice' | 'cost' | 'barcode'>[] }
  | { ok: false; error: string; vars?: Record<string, number> } {
  const options = withPending(draft)
    .map((option) => {
      const swatches = keepFor(option.swatches, option.values)
      const images = keepFor(option.images, option.values)
      return { name: option.name.trim(), values: option.values, ...(swatches ? { swatches } : {}), ...(images ? { images } : {}) }
    })
    .filter((option) => option.name || option.values.length)
  if (options.some((option) => !option.name)) return { ok: false, error: 'offers.variants.nameRequired' }
  if (options.some((option) => !option.values.length)) return { ok: false, error: 'offers.variants.valuesRequired' }
  const combos = combinationsOf(options)
  if (combos.length > MAX_VARIANT_COMBINATIONS) return { ok: false, error: 'offers.variants.tooMany', vars: { max: MAX_VARIANT_COMBINATIONS } }
  const variants = combos.map((values) => {
    const row = draft.rows[variantKeyOf(values)] ?? fresh
    const money = (text: string | undefined) => ((text ?? '').trim() === '' ? null : (toMinorUnits(text ?? '', currency) ?? -1))
    return {
      options: values, stock: row.stock.trim() === '' ? null : Number.parseInt(row.stock, 10), sku: row.sku.trim(), active: row.active,
      price: money(row.price), compareAtPrice: money(row.compareAt), cost: money(row.cost),
      barcode: (row.barcode ?? '').trim(), image: (row.image ?? '').trim() || null,
    }
  })
  if (variants.some((variant) => variant.stock !== null && (!Number.isInteger(variant.stock) || variant.stock < 0))) {
    return { ok: false, error: 'offers.new.shipping.stockInvalid' }
  }
  /* Un precio que no se entiende, o cero: un producto físico no se regala por variante. */
  if (variants.some((variant) => variant.price !== null && variant.price <= 0)) {
    return { ok: false, error: 'offers.variants.priceInvalid' }
  }
  /* El «antes» y el costo pueden ser cero o faltar, pero no ser ilegibles. */
  if (variants.some((variant) => (variant.compareAtPrice ?? 0) < 0 || (variant.cost ?? 0) < 0)) {
    return { ok: false, error: 'offers.variants.priceInvalid' }
  }
  return { ok: true, options, variants }
}

/** Si lo que se guardaría es distinto de lo que ya tiene la oferta. */
export function variantsChanged(draft: VariantsDraft, offer: Pick<Offer, 'variantOptions' | 'variants'> | null | undefined, fresh: VariantRow, currency = 'usd'): boolean {
  const final = finalizeVariants(draft, fresh, currency)
  if (!final.ok) return true
  const saved = finalizeVariants(variantsDraftOf(offer, currency), fresh, currency)
  return !saved.ok || JSON.stringify(final) !== JSON.stringify(saved)
}

// ── Atajos y sugerencias ────────────────────────────────────────────────────

type PresetKind = 'size' | 'color' | 'material'

const PRESETS: { kind: PresetKind; Icon: typeof IconRuler2 }[] = [
  { kind: 'size', Icon: IconRuler2 },
  { kind: 'color', Icon: IconColorSwatch },
  { kind: 'material', Icon: IconBox },
]

/** Qué atajo es una opción por su nombre, en cualquiera de los dos idiomas. */
const PRESET_NAMES: Record<PresetKind, RegExp> = {
  size: /^(talla|tallas|tamaño|medida|size|sizes)$/i,
  color: /^(color|colores|colour|colors|colours)$/i,
  material: /^(material|materiales|materials|tela|fabric)$/i,
}

function presetOf(name: string): PresetKind | null {
  const clean = name.trim()
  return (Object.keys(PRESET_NAMES) as PresetKind[]).find((kind) => PRESET_NAMES[kind].test(clean)) ?? null
}

/*
 * Una muestra para los colores que se nombran igual en todas partes. No es el
 * acento del panel —es el color de lo que se vende—, por eso va escrito.
 */
const SWATCHES: [RegExp, string][] = [
  [/^(negro|black)$/i, '#111111'],
  [/^(blanco|white)$/i, '#ffffff'],
  [/^(gris|gray|grey)$/i, '#9ca3af'],
  [/^(plata|silver)$/i, '#c0c4cc'],
  [/^(rojo|red)$/i, '#dc2626'],
  [/^(vino|burdeos|burgundy|wine)$/i, '#7f1d1d'],
  [/^(rosa|rosado|pink)$/i, '#f472b6'],
  [/^(naranja|orange)$/i, '#f97316'],
  [/^(amarillo|yellow)$/i, '#facc15'],
  [/^(dorado|oro|gold)$/i, '#d4a72c'],
  [/^(verde|green)$/i, '#16a34a'],
  [/^(oliva|olive)$/i, '#6b7a2a'],
  [/^(azul|blue)$/i, '#1d6fdc'], // accent-ok: el color de una prenda, no el del panel
  [/^(azul marino|marino|navy)$/i, '#1e2a4a'],
  [/^(celeste|azul cielo|sky|light blue)$/i, '#7cc4f2'],
  [/^(morado|lila|violeta|purple|violet)$/i, '#7c3aed'],
  [/^(café|cafe|marrón|marron|chocolate|brown)$/i, '#7a4a2a'],
  [/^(beige|crema|arena|cream|sand)$/i, '#e8dcc2'],
]

function swatchOf(value: string): string | null {
  const clean = value.trim()
  return SWATCHES.find(([pattern]) => pattern.test(clean))?.[1] ?? null
}

/** Un trozo de SKU: mayúsculas, sin acentos ni espacios. */
function skuPart(value: string, length: number): string {
  return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().replace(/[^A-Z0-9]+/g, '').slice(0, length)
}

export function VariantsEditor({ draft, onChange, fresh, skuBase = '', basePrice = '', photos = [], projectId, token, currency = 'usd', baseAmount = null }: {
  draft: VariantsDraft
  onChange: (draft: VariantsDraft) => void
  /** Cómo arranca una combinación nueva. */
  fresh: VariantRow
  /** El nombre del producto, para generar los SKU («CAMISETA-M-NEG»). */
  skuBase?: string
  /** El precio del producto ya escrito («$25.00»): lo que cuesta una variante sin precio propio. */
  basePrice?: string
  /** Las fotos del producto: de ellas se elige la de cada variante. */
  photos?: string[]
  /** Para subir una foto nueva desde la ficha de una variante. */
  projectId: string
  token: string
  currency?: string
  /** El precio del producto en centavos, para la ganancia en la ficha. */
  baseAmount?: number | null
}) {
  const { t } = useI18n()
  const { options, rows, pending } = draft
  const combos = combinationsOf(options)
  const rowFor = (values: string[]) => rows[variantKeyOf(values)] ?? fresh
  const valueInputs = useRef<(HTMLInputElement | null)[]>([])
  /* La ficha abierta: una variante (por sus valores) o todas a la vez. */
  const [editing, setEditing] = useState<string[] | 'all' | null>(null)
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})

  const setOptions = (next: OfferVariantOption[], nextPending = pending) => onChange({ ...draft, options: next, pending: nextPending })
  const setOption = (index: number, next: Partial<OfferVariantOption>) =>
    setOptions(options.map((option, at) => (at === index ? { ...option, ...next } : option)))
  const setPendingAt = (index: number, text: string) => {
    const next = [...pending]
    next[index] = text
    onChange({ ...draft, pending: next })
  }
  const commitValues = (index: number) => {
    const option = options[index]
    if (!option || !(pending[index] ?? '').trim()) return
    onChange({
      ...draft,
      options: options.map((row, at) => (at === index ? { ...row, values: addValues(row.values, pending[index] ?? '') } : row)),
      pending: pending.map((text, at) => (at === index ? '' : text)),
    })
  }
  /** El tono o la foto de un valor; vacío lo quita. */
  const setPerValue = (index: number, field: 'swatches' | 'images', value: string, next: string) => {
    const option = options[index]
    if (!option) return
    const map = { ...(option[field] ?? {}) }
    if (next) map[value] = next
    else delete map[value]
    setOption(index, { [field]: Object.keys(map).length ? map : undefined })
  }
  const setAllRows = (next: (values: string[], row: VariantRow) => Partial<VariantRow>) =>
    onChange({ ...draft, rows: { ...rows, ...Object.fromEntries(combos.map((values) => [variantKeyOf(values), { ...rowFor(values), ...next(values, rowFor(values)) }])) } })

  /** Una opción nueva: la de un atajo con su nombre, o una en blanco. */
  const addOption = (kind: PresetKind | null) => {
    const index = options.length
    setOptions([...options, { name: kind ? t(`offers.variants.preset.${kind}`) : '', values: [] }], [...pending, ''])
    requestAnimationFrame(() => {
      const card = document.querySelectorAll<HTMLElement>('.dash-vx-option')[index]
      card?.querySelector<HTMLInputElement>(kind ? '.dash-vx-values input' : '.dash-vx-name')?.focus()
    })
  }

  /** Los atajos que aún no se usan, para la fila de «Añadir opción». */
  const unusedPresets = PRESETS.filter((preset) => !options.some((option) => presetOf(option.name) === preset.kind))

  // El inventario de un vistazo.
  const selling = combos.filter((values) => rowFor(values).active)
  const unlimited = selling.some((values) => rowFor(values).stock.trim() === '')
  const units = selling.reduce((sum, values) => sum + (Number.parseInt(rowFor(values).stock, 10) || 0), 0)
  const grouped = options.filter((option) => option.name.trim() && option.values.length).length > 1
  const groups = grouped
    ? (options.find((option) => option.name.trim() && option.values.length)?.values ?? []).map((first) => ({
      first,
      combos: combos.filter((values) => values[0] === first),
    }))
    : [{ first: '', combos }]
  const colorIndex = options.findIndex((option) => presetOf(option.name) === 'color' || Object.keys(option.swatches ?? {}).length > 0)
  /* La muestra de un valor: el tono elegido o, si no, el que dice su nombre. */
  const toneOf = (index: number, value: string) => options[index]?.swatches?.[value] ?? swatchOf(value)
  /* La foto que se ve de una variante: la suya, la de su color o ninguna. */
  const photoOf = (values: string[]) => rowFor(values).image || (colorIndex >= 0 ? options[colorIndex]?.images?.[values[colorIndex] ?? ''] ?? '' : '')

  const generateSkus = () => {
    const base = skuPart(skuBase, 10) || 'SKU'
    setAllRows((values) => ({ sku: [base, ...values.map((value) => skuPart(value, 4))].filter(Boolean).join('-') }))
  }

  /* Una fila: lo que se lee de un vistazo. Pulsarla abre su ficha. */
  /* Un precio escrito a mano («26», «32») con su moneda, como el resto de la
     tabla: el tachado y el de las variantes con precio propio salían sin el
     signo. Lo que no se pueda leer como importe se enseña tal cual. */
  const moneyText = (text: string) => {
    const amount = toMinorUnits(text, currency)
    return amount === null ? text : formatMoney(amount, currency)
  }

  const renderRow = (values: string[], label: string) => {
    const row = rowFor(values)
    const fullLabel = values.join(' / ')
    const swatch = colorIndex >= 0 ? toneOf(colorIndex, values[colorIndex] ?? '') : null
    const photo = photoOf(values)
    const stock = Number.parseInt(row.stock, 10)
    return (
      <button
        key={variantKeyOf(values)}
        type="button"
        className={`dash-vx-row is-click${row.active ? '' : ' is-off'}`}
        role="row"
        aria-label={t('offers.variants.openVariant', { variant: fullLabel })}
        onClick={() => setEditing(values)}
      >
        <span role="cell" className="dash-vx-row-name">
          {photo ? <img className="dash-vx-row-thumb" src={photo} alt="" loading="lazy" /> : swatch ? <i className="dash-vx-swatch" style={{ background: swatch }} aria-hidden="true" /> : null}
          <strong title={fullLabel}>{label}</strong>
          {row.active && row.stock.trim() !== '' && stock === 0 ? <em className="dash-vx-tag is-out">{t('offers.detail.soldOut')}</em> : null}
          {!row.active ? <em className="dash-vx-tag">{t('offers.variants.notSold')}</em> : null}
        </span>
        <span role="cell" className="dash-vx-cell-price">
          {row.compareAt.trim() ? <s>{moneyText(row.compareAt)}</s> : null}
          {row.price.trim() ? moneyText(row.price) : basePrice || '—'}
        </span>
        <span role="cell" className="dash-vx-cell-stock">{row.stock.trim() === '' ? '∞' : row.stock}</span>
        <span role="cell" className="dash-vx-cell-go" aria-hidden="true"><IconChevronRight size={15} /></span>
      </button>
    )
  }

  /* ── Sin opciones: la portada con atajos ─────────────────────────────── */
  if (!options.length) {
    return (
      <div className="dash-vx">
        <div className="dash-vx-empty">
          <span className="dash-vx-empty-icon" aria-hidden="true"><IconStack2 size={22} /></span>
          <strong>{t('offers.variants.empty.title')}</strong>
          <p>{t('offers.variants.empty.body')}</p>
          <div className="dash-vx-presets">
            {PRESETS.map(({ kind, Icon }) => (
              <button key={kind} type="button" className="dash-vx-preset" onClick={() => addOption(kind)}>
                <Icon size={16} aria-hidden="true" />
                <span>
                  <b>{t(`offers.variants.preset.${kind}`)}</b>
                  <small>{t(`offers.variants.suggest.${kind}`).split(',').slice(0, 3).map((value) => value.trim()).join(' · ')}…</small>
                </span>
              </button>
            ))}
            <button type="button" className="dash-vx-preset" onClick={() => addOption(null)}>
              <IconTag size={16} aria-hidden="true" />
              <span>
                <b>{t('offers.variants.preset.custom')}</b>
                <small>{t('offers.variants.preset.customHint')}</small>
              </span>
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="dash-vx">
      {/* ── Las opciones ───────────────────────────────────────────────── */}
      <div className="dash-vx-options">
        {options.map((option, index) => {
          const kind = presetOf(option.name)
          const taken = new Set(option.values.map((value) => value.toLowerCase()))
          const suggestions = kind
            ? t(`offers.variants.suggest.${kind}`).split(',').map((value) => value.trim()).filter((value) => value && !taken.has(value.toLowerCase()))
            : []
          const isColor = kind === 'color'
          return (
            <article key={index} className="dash-vx-option">
              <header className="dash-vx-option-head">
                <span className="dash-vx-index" aria-hidden="true">{index + 1}</span>
                <input
                  className="dash-vx-name"
                  value={option.name}
                  maxLength={40}
                  placeholder={t('offers.variants.namePlaceholder')}
                  aria-label={t('offers.variants.optionName')}
                  onChange={(event) => setOption(index, { name: event.target.value })}
                />
                <span className="dash-vx-count">
                  {t(option.values.length === 1 ? 'offers.variants.valueCountOne' : 'offers.variants.valueCount', { count: option.values.length })}
                </span>
                <button
                  type="button"
                  className="dash-icon-btn"
                  aria-label={t('offers.variants.removeOption', { name: option.name || t('offers.variants.optionName') })}
                  data-tooltip={t('offers.variants.removeOptionShort')}
                  onClick={() => setOptions(options.filter((_, at) => at !== index), pending.filter((_, at) => at !== index))}
                ><IconTrash size={14} /></button>
              </header>

              {/* Las fichas y el campo, en una sola caja: un clic en cualquier
                  hueco lleva al campo. */}
              <div className="dash-vx-values" onClick={(event) => { if (event.target === event.currentTarget) valueInputs.current[index]?.focus() }}>
                {option.values.map((value) => {
                  const tone = isColor ? toneOf(index, value) : null
                  return (
                    <span key={value} className="dash-vx-chip">
                      {/* La muestra es el selector de tono: pulsarla abre el del sistema. */}
                      {isColor ? (
                        <label className="dash-vx-swatch-pick" title={t('offers.variants.pickTone', { value })}>
                          <i className={`dash-vx-swatch${tone ? '' : ' is-empty'}`} style={tone ? { background: tone } : undefined} aria-hidden="true" />
                          <input
                            type="color"
                            value={tone && /^#[0-9a-f]{6}$/i.test(tone) ? tone : '#cccccc'}
                            aria-label={t('offers.variants.pickTone', { value })}
                            onChange={(event) => setPerValue(index, 'swatches', value, event.target.value)}
                          />
                        </label>
                      ) : null}
                      {value}
                      <button
                        type="button"
                        aria-label={t('offers.variants.removeValue', { value })}
                        onClick={() => {
                          const rest = option.values.filter((candidate) => candidate !== value)
                          setOption(index, { values: rest, swatches: keepFor(option.swatches, rest), images: keepFor(option.images, rest) })
                        }}
                      ><IconX size={11} /></button>
                    </span>
                  )
                })}
                <input
                  ref={(node) => { valueInputs.current[index] = node }}
                  value={pending[index] ?? ''}
                  placeholder={t(option.values.length ? 'offers.variants.addValue' : 'offers.variants.firstValue')}
                  aria-label={t('offers.variants.addValueAria', { name: option.name || t('offers.variants.optionName') })}
                  onChange={(event) => setPendingAt(index, event.target.value)}
                  onKeyDown={(event) => {
                    /* Dentro del asistente esto vive en su <form>: Intro añade el
                       valor, no avanza de paso. */
                    if (event.key === 'Enter' || event.key === ',') { event.preventDefault(); commitValues(index) }
                    if (event.key === 'Backspace' && !(pending[index] ?? '') && option.values.length) {
                      setOption(index, { values: option.values.slice(0, -1) })
                    }
                  }}
                  /* Al salir hacia otro campo se confirma lo escrito. Hacia un
                     botón no: confirmar añade filas a la tabla, empuja el botón
                     entre que se pulsa y se suelta, y el clic se pierde. Lo
                     escrito se confirma igual al cambiar de paso o al guardar. */
                  onBlur={(event) => { if (event.relatedTarget instanceof HTMLInputElement) commitValues(index) }}
                />
                {(pending[index] ?? '').trim() ? (
                  <button type="button" className="dash-vx-enter" onClick={() => commitValues(index)}>
                    <IconCheck size={12} /> {t('offers.variants.addShort')}
                  </button>
                ) : null}
              </div>

              {suggestions.length ? (
                <div className="dash-vx-suggest">
                  <span><IconSparkles size={12} aria-hidden="true" /> {t('offers.variants.suggestions')}</span>
                  {suggestions.map((value) => {
                    const swatch = isColor ? swatchOf(value) : null
                    return (
                      <button key={value} type="button" onClick={() => setOption(index, { values: addValues(option.values, value) })}>
                        {swatch ? <i className="dash-vx-swatch" style={{ background: swatch }} aria-hidden="true" /> : <IconPlus size={11} aria-hidden="true" />}
                        {value}
                      </button>
                    )
                  })}
                  {suggestions.length > 1 ? (
                    <button type="button" className="is-all" onClick={() => setOption(index, { values: addValues(option.values, suggestions.join(',')) })}>
                      {t('offers.variants.addAll')}
                    </button>
                  ) : null}
                </div>
              ) : (
                <p className="dash-vx-hint">{t('offers.variants.valuesHint')}</p>
              )}
            </article>
          )
        })}

        {options.length < MAX_VARIANT_OPTIONS ? (
          <div className="dash-vx-add">
            <span>{t('offers.variants.addOption')}</span>
            {unusedPresets.map(({ kind, Icon }) => (
              <button key={kind} type="button" onClick={() => addOption(kind)}>
                <Icon size={14} aria-hidden="true" /> {t(`offers.variants.preset.${kind}`)}
              </button>
            ))}
            <button type="button" onClick={() => addOption(null)}>
              <IconPlus size={14} aria-hidden="true" /> {t('offers.variants.preset.custom')}
            </button>
          </div>
        ) : null}
      </div>

      {/* ── El inventario ──────────────────────────────────────────────── */}
      {combos.length ? (
        <section className="dash-vx-inventory" aria-label={t('offers.variants.tableAria')}>
          <header className="dash-vx-inventory-head">
            <div>
              <h4>{t('offers.variants.inventory')}</h4>
              <p>
                {t(combos.length === 1 ? 'offers.variants.summaryOne' : 'offers.variants.summary', { count: combos.length })}
                {' · '}
                {unlimited ? t('offers.variants.summaryUnlimited') : t('offers.variants.summaryUnits', { count: units })}
                {selling.length < combos.length ? ` · ${t('offers.variants.summaryOff', { count: combos.length - selling.length })}` : ''}
              </p>
            </div>
            <button type="button" className="dash-secondary-btn dash-vx-edit-all" onClick={() => setEditing('all')}>
              {t('offers.variants.editAll')}
            </button>
          </header>

          <div className="dash-vx-table" role="table">
            <div className="dash-vx-row is-head" role="row">
              <span role="columnheader">{t('offers.variants.colVariant')}</span>
              <span role="columnheader">{t('offers.variants.colPrice')}</span>
              <span role="columnheader">{t('offers.variants.colStock')}</span>
              <span role="columnheader" aria-hidden="true" />
            </div>
            {groups.map((group) => {
              if (!grouped) return group.combos.slice(0, MAX_VARIANT_COMBINATIONS).map((values) => renderRow(values, values.join(' / ')))
              const open = !collapsed[group.first]
              const groupSelling = group.combos.filter((values) => rowFor(values).active)
              const groupUnlimited = groupSelling.some((values) => rowFor(values).stock.trim() === '')
              const groupUnits = groupSelling.reduce((sum, values) => sum + (Number.parseInt(rowFor(values).stock, 10) || 0), 0)
              const swatch = colorIndex === 0 ? toneOf(0, group.first) : null
              return (
                <div key={group.first} className={`dash-vx-group${open ? ' is-open' : ''}`} role="rowgroup">
                  <button
                    type="button"
                    className="dash-vx-group-head"
                    aria-expanded={open}
                    onClick={() => setCollapsed((current) => ({ ...current, [group.first]: open }))}
                  >
                    <IconChevronDown size={14} className="dash-vx-chevron" aria-hidden="true" />
                    {swatch ? <i className="dash-vx-swatch" style={{ background: swatch }} aria-hidden="true" /> : null}
                    <strong>{group.first}</strong>
                    <span>
                      {t(group.combos.length === 1 ? 'offers.variants.summaryOne' : 'offers.variants.summary', { count: group.combos.length })}
                      {' · '}
                      {groupUnlimited ? t('offers.variants.summaryUnlimited') : t('offers.variants.summaryUnits', { count: groupUnits })}
                    </span>
                  </button>
                  {open ? group.combos.map((values) => renderRow(values, values.slice(1).join(' / '))) : null}
                </div>
              )
            })}
          </div>
          {combos.length > MAX_VARIANT_COMBINATIONS
            ? <small className="dash-svc-field-error">{t('offers.variants.tooMany', { max: MAX_VARIANT_COMBINATIONS })}</small>
            : <p className="dash-vx-hint">{t('offers.variants.inventoryHint')}</p>}
        </section>
      ) : null}

      {editing ? (() => {
        const all = editing === 'all'
        const values = all ? null : editing
        const colorValue = values && colorIndex >= 0 ? values[colorIndex] ?? null : null
        const blank: VariantRow = { ...fresh, price: '', stock: '' }
        return (
          <VariantDialog
            mode={all ? 'all' : 'one'}
            label={values ? values.join(' / ') : t('offers.variants.allVariants')}
            tone={colorValue ? toneOf(colorIndex, colorValue) : null}
            row={values ? rowFor(values) : blank}
            currency={currency}
            basePrice={basePrice}
            baseAmount={baseAmount}
            photos={photos}
            projectId={projectId}
            token={token}
            valueLabel={colorValue && combos.filter((combo) => combo[colorIndex] === colorValue).length > 1 ? colorValue : null}
            valuePhoto={colorValue ? options[colorIndex]?.images?.[colorValue] ?? '' : ''}
            onGenerateSkus={generateSkus}
            onClose={() => setEditing(null)}
            onApply={(next, photo) => {
              if (all) {
                /* Para todas: sólo lo que se escribió (vacío = no cambiar el precio;
                   las existencias vacías sí ponen «sin límite» a todas). */
                setAllRows(() => ({ stock: next.stock, ...(next.price.trim() ? { price: next.price } : {}) }))
                return
              }
              if (!values) return
              let nextOptions = options
              let image = next.image
              if (photo) {
                if (photo.scope === 'value' && colorValue) {
                  /* Para todas las de ese color: va en el valor, y las variantes
                     de ese color sueltan la suya. */
                  nextOptions = options.map((option, at) => {
                    if (at !== colorIndex) return option
                    const map = { ...(option.images ?? {}) }
                    if (photo.url) map[colorValue] = photo.url
                    else delete map[colorValue]
                    return { ...option, images: Object.keys(map).length ? map : undefined }
                  })
                  image = ''
                } else {
                  image = photo.url
                }
              }
              const nextRows = { ...rows, [variantKeyOf(values)]: { ...next, image } }
              if (photo?.scope === 'value' && colorValue) {
                for (const combo of combos) {
                  if (combo[colorIndex] !== colorValue) continue
                  const key = variantKeyOf(combo)
                  nextRows[key] = { ...(nextRows[key] ?? fresh), image: '' }
                }
              }
              onChange({ ...draft, options: nextOptions, rows: nextRows })
            }}
          />
        )
      })() : null}
    </div>
  )
}
